import React from "react";
import { Button, Callout, Card, DataTable, DisabledReason, EmptyState, PageHeader, ProgressBar, Stat, Timeline } from "../components/DesignSystem";
import type { Scope } from "../domain/types";
import type { RouteKey } from "../shell/PlannerShell";
import { useDashboard, usePlannerData } from "../state/PlannerDataContext";
import { ErrorState, LoadingState } from "./common";

export function HomeScreen({ scope, go }: { scope: Scope; go: (route: RouteKey) => void }) {
  const { workspace, service } = usePlannerData();
  const { summary, loading, error } = useDashboard(scope);
  const [isWeddingDay, setIsWeddingDay] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    void service.dayOf.getContext().then((context) => {
      if (!cancelled) setIsWeddingDay(Boolean(context.todayEventId));
    }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [service]);

  if (loading) return <div className="pob-pad"><LoadingState label="Loading dashboard" /></div>;
  if (error || !summary) return <div className="pob-pad"><ErrorState message={error || "Dashboard unavailable."} /></div>;

  return (
    <div>
      {isWeddingDay && (
        <Callout tone="success" title="It's the big day!" action={<Button size="sm" variant="primary" onClick={() => go("dayof")}>Open Wedding Day Mode</Button>} style={{ margin: "var(--space-4) var(--space-4) 0" }}>
          Timeline, contacts, transport and outstanding tasks for today's celebration — all in one place.
        </Callout>
      )}
      <PageHeader
        eyebrow={scope === "all" ? workspace?.wedding.coupleName || "Whole wedding" : summary.subtitle}
        title={summary.title}
        meta={[
          { icon: "calendar-days", label: summary.subtitle },
          { icon: "users", label: `${summary.totalGuests} guests in this view` },
          { icon: "house", label: `${summary.totalHouseholds} households` }
        ]}
        actions={<><DisabledReason reason="Plan sharing is waiting for the messaging/export API."><Button icon="share-2" disabled>Share plan</Button></DisabledReason><Button variant="primary" icon="plus" onClick={() => go("events")}>Add</Button></>}
      />
      <div className="pob-pad">
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between" }}>
          <span className="pob-eyebrow">Needs your attention</span>
          <span style={{ font: "var(--type-body-sm)", color: "var(--text-tertiary)" }}>{summary.warnings.length} shown</span>
        </div>
        {summary.warnings.length === 0 ? (
          <Card><EmptyState compact icon="check" title="Nothing needs attention" description="Every warning for this scope has been resolved." /></Card>
        ) : summary.warnings.map((warning) => (
          <Callout key={warning.key} tone={warning.tone} title={warning.title} action={<Button size="sm" variant="primary" onClick={() => go(warning.route as RouteKey)}>{warning.actionLabel}</Button>}>
            {warning.body}
          </Callout>
        ))}
        <div className="pob-grid-4">
          <Card padding="var(--space-5)"><Stat label="Guests" value={summary.totalGuests} caption="Service-derived count" /></Card>
          <Card padding="var(--space-5)"><Stat label="Attending" value={summary.attending} caption={`${summary.pendingPeople} pending`} /></Card>
          <Card padding="var(--space-5)"><Stat label="Pending households" value={summary.pendingHouseholds} /></Card>
          <Card padding="var(--space-5)"><Stat label="Partial" value={summary.partialHouseholds} delta="distinct from pending" /></Card>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: "var(--space-4)", alignItems: "start" }}>
          <Card eyebrow="Celebrations" title={scope === "all" ? "Where each one stands" : summary.title} padding="0">
            <DataTable
              dense
              rows={summary.eventSummaries.map((event) => ({ ...event, id: event.eventId }))}
              onRowClick={() => go("events")}
              columns={[
                { key: "name", header: "Celebration" },
                { key: "date", header: "Date", muted: true },
                { key: "venue", header: "Venue", muted: true },
                { key: "attending", header: "Attending", align: "right" },
                { key: "pending", header: "Pending", align: "right" },
                { key: "progress", header: "Replied", width: 180, render: (event) => <ProgressBar value={event.attending + event.declined} max={event.invited || 1} height={5} /> }
              ]}
            />
          </Card>
          <Card eyebrow="Activity" title="What just changed">
            {summary.recentActivity.length === 0
              ? <p style={{ font: "var(--type-body-sm)", color: "var(--text-secondary)" }}>No activity yet.</p>
              : <Timeline items={summary.recentActivity.map((activity) => ({ title: activity.text, date: activity.when, state: "done" }))} />}
          </Card>
        </div>
      </div>
    </div>
  );
}
